/* eslint-disable @typescript-eslint/no-explicit-any */

import IconAngleBottom from '@assets/icons/angle-bottom.svg?react';
import IconAngleTop from '@assets/icons/angle-top.svg?react';
import IconClose from '@assets/icons/close.svg?react';
import IconDashboard from '@assets/icons/dashboard.svg?react';
import IconData from '@assets/icons/data.svg?react';
import IconInfo from '@assets/icons/info.svg?react';
import IconMeter from '@assets/icons/meter.svg?react';
import IconSettings from '@assets/icons/settings.svg?react';
import Logo from '@assets/logo.svg?react';
import LogoDark from '@assets/logo-dark.svg?react';
import IconArrow from '@assets/icons/big arrow left.svg?react';
import LogoSmall from '@assets/logo-small.svg?react';
import {
 Box,
 Button,
 Chip,
 Collapse,
 Drawer,
 IconButton,
 List,
 ListItem,
 ListItemButton,
 ListItemIcon,
 ListItemText,
 Theme,
 useColorScheme,
 useMediaQuery,
 useTheme,
} from '@mui/material';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import buildversion from '../../../../buildversion?raw';

import layoutSizes from '../../../utils/layoutSizes';
import themeOptions from '../../../utils/theme';
import LazySvg from '../LazySvg';
import { useAuthContext } from '@/contexts/AuthContext';

type MenuSidebarProps = {
 isSidebarOpen: boolean;
 setIsSidebarOpen: Dispatch<SetStateAction<boolean>>;
 isAdmin?: boolean;
};

const MenuSidebar = ({
 isSidebarOpen,
 setIsSidebarOpen,
 isAdmin = false,
}: MenuSidebarProps) => {
 const location = useLocation();
 const appVersion = buildversion.trim();

 const { mode, systemMode } = useColorScheme();
 const resolvedMode = mode === 'system' ? systemMode : mode;

 const isLargerThanMd = useMediaQuery(themeOptions().breakpoints.up('md'));

 const colorTheme = useTheme();
 const { palette } = colorTheme;

 const { t } = useTranslation();
 const { hasRole } = useAuthContext();

 const filterItemsByRoles = (items: any[]) =>
  items.filter((item: any) => {
   if (!item?.role || typeof item.role !== 'string') {
    return true;
   }

   const [resourceName, actionName] = item.role.split('.');

   if (!resourceName || !actionName) {
    return true;
   }

   return hasRole(resourceName, actionName);
  });

 const filterMenuByRoles = (menuCategories: any[]) =>
  menuCategories
   .map((menuCategory) => {
    const filteredItems = filterItemsByRoles(menuCategory.items || []);

    return {
     ...menuCategory,
     items: filteredItems,
    };
   })
   .filter(
    (menuCategory) =>
     Array.isArray(menuCategory.items) && menuCategory.items.length > 0,
   );

 const menuItemsData = useMemo(() => {
  if (isAdmin) {
   return filterMenuByRoles([
    // {
    //  title: t('menu.dashboard'),
    //  Icon: IconDashboard,
    //  open: false,
    // items: [
    //  {
    //   url: '/admin/dashboard',
    //   title: t('menu.dashboard'),
    //   role: 'Results.GetAll',
    //  },
    // ],
    // },
    // {
    //  title: t('menu.settings'),
    //  Icon: IconSettings,
    //  open: false,
    // items: [
    //  {
    //   url: '/admin/licenses',
    //   title: t('menu.licenses'),
    //   role: 'Licenses.GetAll',
    //  },
    // ],
    // },
    {
     title: t('menu.users'),
     Icon: IconData,
     open: false,
     items: [
      { url: '/admin/users', title: t('menu.users'), role: 'Users.GetAll' },
      {
       url: '/admin/accessGroups',
       title: t('menu.accessGroups'),
       role: 'AccessGroups.GetAll',
      },
     ],
    },
    {
     title: t('menu.devices'),
     Icon: IconMeter,
     open: false,
     items: [
      {
       url: '/admin/devices',
       title: t('menu.devices'),
      },
     ],
    },
    {
     title: t('menu.audit'),
     Icon: IconInfo,
     open: false,
     items: [
      {
       url: '/admin/audit',
       title: t('menu.audit'),
       role: 'Audit.GetAll',
      },
     ],
    },
    // {
    //  title: t('menu.history'),
    //  Icon: IconInfo,
    //  open: false,
    // items: [
    //  {
    //   url: '/admin/history',
    //   title: t('menu.history'),
    //   role: 'History.GetAll',
    //  },
    // ],
    // },
   ]);
  }

  return filterMenuByRoles([
   {
    title: t('entities.dashboard'),
    Icon: IconDashboard,
    open: false,
    items: [{ url: '/dashboard', title: 'Dashboard', role: 'Results.GetAll' }],
   },
   {
    title: t('entities.data'),
    Icon: IconData,
    open: true,
    items: [
     {
      url: '/results',
      title: t('entities.results'),
      role: 'Results.GetAll',
     },
     {
      url: '/customers',
      title: t('entities.customers'),
      role: 'Customers.GetAll',
     },
     {
      url: '/orders',
      title: t('entities.orders'),
      role: 'Orders.GetAll',
     },
     {
      url: '/drivers',
      title: t('entities.drivers'),
      role: 'Drivers.GetAll',
     },
     {
      url: '/vehicles',
      title: t('entities.vehicles'),
      role: 'Vehicles.GetAll',
     },
     {
      url: '/products',
      title: t('entities.products'),
      role: 'Products.GetAll',
     },
     {
      url: '/destPoint',
      title: t('entities.destPoint'),
      role: 'DestPoint.GetAll',
     },
     {
      url: '/cards',
      title: t('entities.cards'),
      role: 'Cards.GetAll',
     },
     {
      url: '/warehouse',
      title: t('entities.warehouse'),
      role: 'Warehouse.GetAll',
     },
     {
      url: '/inventorization',
      title: t('entities.inventorization'),
      role: 'Inventorization.GetAll',
     },
     {
      url: '/spy',
      title: t('entities.spy'),
      role: 'Spy.GetAll',
     },
    ],
   },
  ]);
 }, [isAdmin, t, hasRole]);

 const getMenuItemsWithActiveOpen = (items: any[]) =>
  items.map((menuItem) => ({
   ...menuItem,
   open:
    menuItem.open ||
    menuItem.items?.some((item: any) => item.url === location.pathname),
  }));

 const [menuItems, setMenuItems] = useState(() =>
  getMenuItemsWithActiveOpen(menuItemsData),
 );

 useEffect(() => {
  setMenuItems((previousMenuItems) =>
   menuItemsData.map((menuItem, index) => {
    const hasActiveItem = menuItem.items?.some(
     (item: any) => item.url === location.pathname,
    );

    return {
     ...menuItem,
     open: hasActiveItem || previousMenuItems[index]?.open || menuItem.open,
    };
   }),
  );
 }, [location.pathname, menuItemsData]);

 const handleMenuItemClick = (index: number) => {
  const newMenuItems = [...menuItems];

  newMenuItems[index].open = !newMenuItems[index].open;

  setMenuItems(newMenuItems);
 };
 return (
  <Drawer
   sx={{
    flexShrink: 0,
    '& .MuiPaper-root': {
     boxShadow: 'none',
    },
    '& .MuiDrawer-paper': (theme: Theme) => ({
     border: 0,
     background:
      theme.palette.mode === 'dark' ? '#15181D' : theme.palette.kemek.bg1,
     //  boxSizing: 'border-box',
     width: isSidebarOpen
      ? { xs: '100%', md: layoutSizes.sidebarWidth }
      : { xs: 0, md: 76 },
    }),
    '& .MuiListItemIcon-root ': {
     mr: 2,
     minWidth: 32,
     height: 32,
     display: 'flex',
     alignItems: 'center',
     justifyContent: 'center',
    },
   }}
   variant="permanent"
   anchor="left"
   elevation={0}
  >
   <Box
    sx={{
     height: '72px',
     display: 'flex',
     alignItems: 'center',
     justifyContent: 'center',
     //  padding: '21px',
     borderBottom: (theme: Theme) =>
      `1px solid ${theme.palette.kemek.lightestGray}`,
    }}
   >
    <IconButton
     onClick={() => setIsSidebarOpen(!isSidebarOpen)}
     sx={{
      display: { xs: 'block', md: 'none' },
      position: 'absolute',
      right: 16,
      top: 16,
     }}
    >
     <IconClose />
    </IconButton>
    {resolvedMode === 'light' && (
     <Link to="/results">
      {isSidebarOpen ? (
       <Logo style={{ display: 'block' }} />
      ) : (
       <LogoSmall style={{ display: 'block' }} />
      )}
     </Link>
    )}
    {resolvedMode === 'dark' && (
     <Link to="/results">
      {isSidebarOpen ? (
       <LogoDark style={{ display: 'block' }} />
      ) : (
       <LogoSmall style={{ display: 'block' }} />
      )}
     </Link>
    )}
   </Box>

   {isAdmin && isSidebarOpen && (
    <Box sx={{ px: 2, pt: 4 }}>
     <Chip
      label={t('menu.administrative')}
      color="default"
      sx={{
       background: 'transparent',
       border: '1px solid',
       fontWeight: 500,
       borderColor: (theme: Theme) => theme.palette.kemek.lightGray,
       color: (theme: Theme) => theme.palette.kemek.darkestGray,
      }}
      size="small"
      className="mt-1"
     />
    </Box>
   )}

   <Box
    sx={{
     p: 2,
     display: 'flex',
     flexDirection: 'column',
     flex: 1,
    }}
   >
    <List
     sx={{
      padding: 0,
      // marginBottom: 'auto',
      // flex: 1,
     }}
    >
     {!isSidebarOpen &&
      menuItems.map(({ title, Icon, open, items }, index) => {
       const hasActiveItem = items?.some(
        (item: any) => item.url === location.pathname,
       );

       return (
        <Box key={title}>
         <ListItemButton
          onClick={() => {
           setIsSidebarOpen(true);
           handleMenuItemClick(index);
          }}
          sx={{
           padding: '12px',
           justifyContent: 'center',
           height: 44,
           width: 44,
           background: hasActiveItem ? palette.kemek.blue : 'transparent',
           borderRadius: '4px',
          }}
         >
          <ListItemIcon sx={{ mr: '0 !important' }}>
           <Icon
            style={{
             color: hasActiveItem ? palette.kemek.white : 'initial',
            }}
           />
          </ListItemIcon>
         </ListItemButton>
        </Box>
       );
      })}

     {isSidebarOpen &&
      menuItems.map(({ title, Icon, open, items }, index) => (
       <Box key={title}>
        <ListItemButton
         onClick={() => handleMenuItemClick(index)}
         sx={{ pl: '12px' }}
        >
         <ListItemIcon>
          <Icon style={{ color: palette.kemek.darkestGray }} />
         </ListItemIcon>

         <ListItemText primary={title} />

         {open ? (
          <IconAngleTop style={{ color: palette.kemek.darkestGray }} />
         ) : (
          <IconAngleBottom style={{ color: palette.kemek.darkestGray }} />
         )}
        </ListItemButton>

        {items &&
         items.map((item: any) => (
          <Collapse
           in={open}
           timeout="auto"
           unmountOnExit
           key={`${item.title}`}
          >
           <List sx={{ padding: 0 }}>
            <ListItem component="div" disablePadding>
             {typeof item?.url !== 'undefined' ? (
              <Link
               style={{ width: '100%' }}
               to={item?.url}
               onClick={() => {
                if (!isLargerThanMd) {
                 setIsSidebarOpen(false);
                }
               }}
              >
               <ListItemButton
                sx={{
                 pl: 2,
                 background:
                  location.pathname === item.url
                   ? palette.kemek.blue
                   : 'transparent',
                 borderRadius: '4px',
                 maxHeight: 42,
                 '&:hover': {
                  background: `${
                   location.pathname === item.url
                    ? palette.kemek.blue
                    : palette.kemek.lightestGray
                  } !important`,
                 },
                }}
               >
                <ListItemIcon>
                 {item.icon && <LazySvg name={item.icon} />}
                </ListItemIcon>

                <ListItemText
                 primaryTypographyProps={{
                  style: {
                   color:
                    location.pathname === item.url
                     ? 'white'
                     : palette.kemek.darkestGray,
                   fontWeight: 400,
                   fontSize: 12,
                  },
                 }}
                 primary={item.title}
                />
               </ListItemButton>
              </Link>
             ) : (
              <ListItemButton sx={{ pl: 2 }}>
               <ListItemIcon>
                {item.icon && <LazySvg name={item.icon} />}
               </ListItemIcon>

               <ListItemText primary={item.title} />
              </ListItemButton>
             )}
            </ListItem>
           </List>
          </Collapse>
         ))}
       </Box>
      ))}
    </List>

    {isAdmin && isSidebarOpen && (
     <Box sx={{ pt: 2 }}>
      <Button
       startIcon={<IconArrow />}
       component={Link}
       to="/results"
       variant="text"
       color="secondary"
       fullWidth
      >
       {t('admin.goBackToClient')}
      </Button>
     </Box>
    )}

    <Box sx={{ flex: 1 }}>{/* empty space */}</Box>

    <Box
     sx={{
      display: { xs: 'none', md: 'flex' },
      alignItems: 'center',
      fontSize: 12,
      mt: '20px',
      color: (theme: Theme) => theme.palette.kemek.lightGray,
     }}
    >
     {appVersion || '-'}
    </Box>

    {/* {isSidebarOpen && <SidebarSpace />} */}
   </Box>
  </Drawer>
 );
};

export default MenuSidebar;
